import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { LayoutDashboard, Users, Files, ArrowLeftRight, History, ShieldCheck, Activity, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

import { getTransactionById, updateTransaction } from '../../features/transactions/api/transactionsAPI';
import { useNotification } from '../../context/NotificationContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tooltip from '../../components/ui/Tooltip';

import TransactionHeader from '../../features/transactions/components/TransactionHeader';
import TransactionMetrics from '../../features/transactions/components/TransactionMetrics';
import TransactionTabs from '../../features/transactions/components/TransactionTabs';
import TransactionTabContent from '../../features/transactions/components/TransactionTabContent';
import CustomSelect from '../../components/ui/CustomSelect';
import { TRANSACTION_STATUSES_LABELS } from '../../constants/transactionStatuses';
import NotFound from '../errors/NotFound';
import Unauthorized from '../errors/Unauthorized';

export default function TransactionDetail() {
  const { id } = useParams();
  const { showToast } = useNotification();

  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchTransaction = useCallback(async () => {
    setLoading(true);
    setErrorStatus(null);
    try {
      const data = await getTransactionById(id);
      setTransaction(data);
    } catch (error) {
      console.error("Erreur récupération transaction :", error);
      const status = error.response?.status;
      setErrorStatus(status || 404);
      if (status !== 404 && status !== 401 && status !== 403) {
        showToast("Impossible de charger la transaction.", "error");
      }
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    fetchTransaction();
  }, [fetchTransaction]);

  const handleStatusChange = async (newStatus) => {
    if (!newStatus || newStatus === transaction.status) {
      return;
    }
    setUpdatingStatus(true);
    try {
      await updateTransaction(id, { status: newStatus });
      setTransaction((current) => ({
        ...current,
        status: newStatus
      }));
      showToast("Statut mis à jour avec succès.", "success");
    } catch (error) {
      showToast("Impossible de mettre à jour le statut.", "error");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center bg-workspace">
        <Activity className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (errorStatus === 401 || errorStatus === 403) {
    return <Unauthorized />;
  }

  if (!transaction || errorStatus === 404) {
    return <NotFound />;
  }

  const tabs = [
    {
      id: "overview",
      label: "Vue d'ensemble",
      icon: LayoutDashboard
    },
    {
      id: "participants",
      label: "Intervenants",
      count: transaction.participants?.length || 0,
      icon: Users
    },
    {
      id: "documents",
      label: "Documents",
      count: transaction.documents?.length || 0,
      icon: Files
    },
    {
      id: "flows",
      label: "Flux",
      count: transaction.flows?.length || 0,
      icon: ArrowLeftRight
    },
    {
      id: "history",
      label: "Historique",
      icon: History
    },
    {
      id: "access",
      label: "Accès",
      icon: ShieldCheck
    }
  ];

  const statusOptions = Object.entries(TRANSACTION_STATUSES_LABELS).map(([value, label]) => ({
    value,
    label
  }));

  return (
    <div className="flex h-full flex-col overflow-hidden bg-workspace">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mx-auto flex h-full w-full flex-col px-2 sm:px-4 lg:px-4"
      >
        {/* En-tête fixe (ne scrolle pas) */}
        <div className="shrink-0 space-y-4 pt-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <Breadcrumbs
              items={[
                { label: "Opérations", path: "/transactions" },
                { label: transaction.reference }
              ]}
            />
            
            {/* Stade de l'opération (Design identique à DossierDetail) */}
            <div className="flex items-center h-10 bg-surface rounded-xl border border-workspace shadow-sm focus-within:ring-2 focus-within:ring-[var(--hp-bronze-500-rgb)]/20 transition-all duration-300 group">
              <div className="flex items-center gap-2 px-3.5 h-full bg-surface-muted border-r border-workspace rounded-l-xl transition-colors group-hover:bg-workspace">
                <RefreshCw className={`h-4 w-4 text-tertiary ${updatingStatus ? 'animate-spin' : ''}`} strokeWidth={2} />
                <Tooltip content="Modification du stade de l'opération">
                  <span className="text-xs font-bold uppercase tracking-wider text-tertiary hidden sm:block cursor-help border-b border-dashed border-tertiary">Stade</span>
                </Tooltip>
              </div>
              <div className="w-52 h-full flex items-center">
                <CustomSelect
                  value={transaction.status}
                  onChange={(val) => handleStatusChange(typeof val === 'string' ? val : val?.target?.value || val)}
                  options={statusOptions}
                  disabled={updatingStatus}
                  className="h-full w-full"
                  triggerClassName="!border-0 !shadow-none !bg-transparent !h-full !min-h-0 !py-0 !px-3.5 flex items-center text-sm font-semibold text-primary"
                />
              </div>
            </div>
          </div>

          <TransactionHeader
            transaction={transaction}
          />

          <TransactionMetrics transaction={transaction} />

          <TransactionTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />
        </div>

        {/* Contenu de l'onglet scrollable */}
        <div className="min-h-0 flex-1 overflow-y-auto pb-6 pr-1 pt-4">
          <TransactionTabContent
            activeTab={activeTab}
            transaction={transaction}
            onRefresh={fetchTransaction}
          />
        </div>
      </motion.div>
    </div>
  );
}
